import { useAuth0 } from "@auth0/auth0-react";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { Text } from "components";
import AlertDialog from "components/Dialog/index";
import AuthService from "Lib/API/Auth";
import { toggleDrawer } from "pages/Drawer/drawerSlice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Image } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { ReactComponent as NotificationsIcon } from "../assets/icons/notification.svg";

import { AsyncAutoCompleteField } from "components/autoCompleteField/AsyncAutoField";
import { Formik } from "formik";
import { useSearch } from "hook";
import MemberService from "Lib/API/Member";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import LogOutSvg from "../assets/icons/svg/logout";
import { HeaderLogo, HeaderLogoInBase } from "../store/redux/actions";
import HelpLinks from "./HelpLinks/HelpLinks";
import Notifications from "./Notifications/Notifications";
import { addRecentSearch, clearRecentSearches, getRecentSearches } from "./recentSearches";
import { isPageResultVisible, searchPages } from "./pagesSearch";
import { isSettingsResultVisible, searchSettings } from "./settingsSearch";
import "./style.css";

// The backend search runs from 3 letters (or "#" + 1 character for an id)
const needsBackendSearch = (text) =>
  !!text && (text.length > 2 || (text.startsWith("#") && text.length > 1));

const SEARCH_HINT_OPTIONS = [{ id: "", name: "Type 3 letters to start searching" }];

const styles = {
  body: {
    zIndex: 1003,
    minHeight: 70,
  },
  imgBody: {
    borderRadius: "50%",
    background: "#B5B5C3",
    width: 49,
    height: 49,
    marginInlineEnd: 10,
  },
  nameProfileText: {
    color: "#464E5F",
    fontWeight: 600,
    fontSize: 14,
  },
  userNameContainer: {
    marginTop: -5,
  },
  userNameProfileText: {
    color: "#7B7B8B",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  tagProfileText: {
    color: "#464E5F",
    fontWeight: 600,
    fontSize: 14,
  },
};
const originUrl = window.location.origin;

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <div
    className="btn p-0 d-flex align-items-center"
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    ref={ref}
  >
    <div className="px-3" style={{ textAlign: "start" }}>
      {children}
    </div>
    <div
      className="p-3"
      style={{ borderInlineStart: "1px solid #F3F6F9", height: "100%" }}
    >
      &#x25bc;
    </div>
  </div>
));

const MenuHeader = ({ openNotificationSettings }) => {
  const { logout, user } = useAuth0();
  const history = useHistory();

  const onLogOut = () => {
    localStorage.setItem("isLoggedIn", false);
    localStorage.removeItem("token");
    logout({ returnTo: originUrl });
  };

  const firstName = localStorage.getItem("firstName")
    ? JSON.parse(localStorage.getItem("firstName"))
    : null;
  const lastName = localStorage.getItem("lastName")
    ? JSON.parse(localStorage.getItem("lastName"))
    : null;
  const email_address = localStorage.getItem("email_address")
    ? JSON.parse(localStorage.getItem("email_address"))
    : null;
  const userProfileImage = localStorage.getItem("user_profile_image")
    ? JSON.parse(localStorage.getItem("user_profile_image"))
    : null;

  return (
    <Dropdown className="dropdown-header dropdown-header__user">
      <Dropdown.Toggle variant="outline" as={CustomToggle}>
        <div className="d-flex">
          <div
            style={{
              width: 49,
              height: 49,
              marginInlineEnd: 10,
              flexShrink: 0,
            }}
          >
            {userProfileImage || user.picture ? (
              <img
                alt="profile-img"
                src={userProfileImage || user.picture}
                style={{ ...styles.imgBody, margin: 0 }}
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "block";
                }}
              />
            ) : null}
            <AccountCircleIcon
              style={{
                width: 49,
                height: 49,
                color: "#99ABBE",
                display: userProfileImage || user.picture ? "none" : "block",
              }}
            />
          </div>
          <div className="pr-5 align-self-center">
            <div>
              <Text
                className="text-start"
                style={{ ...styles.nameProfileText }}
              >
                {firstName && lastName
                  ? `${firstName} ${lastName}`
                  : user.nickname}
              </Text>
            </div>
            <div style={{ ...styles.userNameContainer }}>
              <Text style={{ ...styles.userNameProfileText }}>
                {email_address || user.email}
              </Text>
            </div>
          </div>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu style={{ width: "280px" }}>
        <Dropdown.Item
          className="d-flex align-items-center notification-settings-item"
          onClick={openNotificationSettings}
        >
          <NotificationsIcon className="mr-3" /> Notification settings
        </Dropdown.Item>
        <Dropdown.Item onClick={onLogOut}>
          <LogOutSvg className="mr-3" /> Log Out
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export const Header = ({
  style,
  openAddNotify,
  openNotificationSettings,
  isNotificationLoading,
  notifications,
  totalNotifications,
  markAsRead,
  markAllAsRead,
  fetchMoreNotifications,
  hasMore,
  replaceLinksHandler,
  ...rest
}) => {
  const { isAuthenticated, user } = useAuth0();
  const recentSearchesKey = user?.sub || user?.email;
  const [recentSearches, setRecentSearches] = useState([]);
  const { setMourners } = { ...rest?.values?.functions };
  const formik = { ...rest };
  // `search` is updated 1 second after typing stops (it triggers the backend search);
  // `typedSearch` is the text in the box right now and drives what the dropdown shows
  const [search, setSearch] = useState(undefined);
  const [typedSearch, setTypedSearch] = useState("");
  const { getAccessTokenSilently } = useAuth0();
  const { searchAction } = useSearch({ action: setSearch });
  const [openConfirmMarkAsRead, setOpenConfirmMarkAsRead] = useState(false);
  // backend (member / contact / finance / yahrzeit) results, and the query they were fetched for
  const [contactsList, setContactsList] = React.useState([]);
  const [backendSearchDone, setBackendSearchDone] = useState(undefined);
  const [email, setEmail] = useState(null);
  const [companyLogo, setCompanyLogo] = useState("");
  const dispatch = useDispatch();

  const HeaderLogoImage = useSelector((state) => state.homeReducer.headerLogo);

  useEffect(() => {
    toDataUrl(HeaderLogoImage, function (myBase64) {
      return localStorage?.setItem("Base64", myBase64);
    });
  }, [HeaderLogoImage]);

  function toDataUrl(url, callback) {
    if (url !== null) {
      var xhr = new XMLHttpRequest();
      xhr.onload = function () {
        var reader = new FileReader();
        reader.onloadend = function () {
          callback(reader.result);
        };
        reader.readAsDataURL(xhr.response);
      };
      xhr.open("GET", url);
      xhr.responseType = "blob";
      xhr.send();
    }
  }

  const handelTogggleDrawer = () => {
    dispatch(toggleDrawer({ type: "left" }));
  };

  const { push } = useHistory();
  const history = useHistory();

  // the newest search text, so an older (slower) response can't overwrite a newer one
  const latestSearchRef = useRef(search);
  latestSearchRef.current = search;

  useEffect(() => {
    const query = search;
    const fetchMyAPI = async () => {
      let results = [];
      try {
        const token = await getAccessTokenSilently();
        const res = await MemberService.searchEverything({ search: query }, token);
        results = (res?.result || []).map((item) => ({
          ...item,
          id: item.member_id,
          label: query,
          name: item?.name,
          value: item?._id,
          profile_img: item?.type,
        }));
        if (!results.length && latestSearchRef.current === query) setEmail(query);
      } catch (error) {
        // the backend search failed: once it's done the dropdown shows the Pages / Settings results on their own
        results = [];
      }
      if (latestSearchRef.current !== query) return;
      setContactsList(results);
      setBackendSearchDone(query);
    };
    if (needsBackendSearch(query)) {
      fetchMyAPI();
    }
  }, [search]);

  // Backend results only belong to the text they were fetched for. From the moment that text
  // is typed until its response (or error) arrives the dropdown shows the loader, so backend
  // and Pages / Settings results always appear together.
  const waitingForBackend = needsBackendSearch(typedSearch) && backendSearchDone !== typedSearch;
  const backendResults = needsBackendSearch(typedSearch) && backendSearchDone === typedSearch ? contactsList.filter((item) => item?.type) : [];

  useEffect(() => {
    setRecentSearches(getRecentSearches(recentSearchesKey));
  }, [recentSearchesKey]);

  // Pages / tabs and Settings pages matching the typed text (only those the user is allowed to open)
  // (matched in the browser from the typed text; only listed once the backend search for it is done)
  const pageResults = useMemo(() => searchPages(typedSearch), [typedSearch]);
  const settingsResults = useMemo(() => searchSettings(typedSearch), [typedSearch]);

  const isNavigationItem = (item) => item?.type === "settings" || item?.type === "page";

  const toSearchOption = (item) => ({
    ...item,
    id: isNavigationItem(item) ? undefined : item?.member_id,
    label: typedSearch || item?.name,
    value: isNavigationItem(item) ? `${item?.type}:${item?.url}:${item?.name}` : item?._id,
    profile_img: item?.type,
    description: item?.type === "settings" ? `Settings · ${item?.section}` : item?.type === "page" ? item?.section : undefined,
  });

  const searchOptions = useMemo(() => {
    // empty search box: recent searches from this browser
    if (!typedSearch) {
      // page / Settings items are only listed while the user can still open them
      const visibleRecentSearches = recentSearches.filter(
        (item) => (item?.type !== "settings" || isSettingsResultVisible(item)) && (item?.type !== "page" || isPageResultVisible(item))
      );
      if (!visibleRecentSearches.length) return SEARCH_HINT_OPTIONS;
      return [
        {
          label: "Recent",
          options: [
            ...visibleRecentSearches.map((item) => ({ ...toSearchOption(item), label: "", value: `recent:${item?.type}:${item?._id || item?.url}:${item?.name}` })),
            { value: "clear-recent-searches", label: "", name: "Clear recent searches", isClearRecentSearches: true },
          ],
        },
      ];
    }
    // Pages / Settings results are only shown together with the backend results: too short
    // for the backend search keeps the hint (no Pages flashing while the first letters are typed),
    // and while the backend search runs only the loader is shown
    if (!needsBackendSearch(typedSearch)) return SEARCH_HINT_OPTIONS;
    if (waitingForBackend) return [];
    if (!pageResults.length && !settingsResults.length) return backendResults;
    const groups = [];
    if (backendResults.length) groups.push({ label: "Results", options: backendResults });
    if (pageResults.length) {
      groups.push({
        label: "Pages",
        options: pageResults.map((entry) => toSearchOption({ type: "page", name: entry.title, url: entry.url, section: entry.section })),
      });
    }
    if (settingsResults.length) {
      groups.push({
        label: "Settings",
        options: settingsResults.map((entry) => toSearchOption({ type: "settings", name: entry.title, url: entry.url, section: entry.section })),
      });
    }
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typedSearch, pageResults, settingsResults, contactsList, backendSearchDone, recentSearches]);

  const searchResultUrl = (e) => {
    if (isNavigationItem(e)) return e?.url;
    if (e?.type === "contact") return `/member-details/${e?.account_id}?q=${e?._id}&tab=Family`;
    if (e?.type === "finance") return `/finance?chargeId=${e?._id}`;
    if (e?.type === "deceased") return `/yahrzeit?q=${e?._id}`;
    if (e?.type === "account") return `/member-details/${e?._id}`;
    return "";
  };

  useEffect(() => {
    async function fetchMyAPI() {
      const token = await getAccessTokenSilently();
      AuthService.getUserExist(token).then((res) => {
        dispatch(HeaderLogo(res?.user?.permissions?.organization?.logoImg));
      });
    }
    fetchMyAPI();
  }, [getAccessTokenSilently]);

  const logoImg = () => {
    setCompanyLogo(localStorage.getItem("logoImg"));
  };

  useEffect(() => {
    window.addEventListener("logoImg", logoImg);
    return () => {
      window.removeEventListener("logoImg", logoImg);
    };
  }, []);

  if (
    !isAuthenticated
    // ||
    // (!location.pathname.includes(screens.HOME) &&
    //   !location.pathname.includes(screens.ADD_NEW_MEMBER) &&
    //   !location.pathname.includes(screens.YAHRZEIT) &&
    //   !location.pathname.includes(screens.ADD_NEW_YAHRZEIT))
  )
    return null;

  const openConfirmMarkAsReadHandler = () => {
    setOpenConfirmMarkAsRead(true);
  };

  const closeConfirmMarkAsReadHandler = () => {
    setOpenConfirmMarkAsRead(false);
  };

  const markAllAsReadHandler = () => {
    markAllAsRead();
    closeConfirmMarkAsReadHandler();
  };

  return (
    <>
      <header
        className="position-sticky header-main top-0"
        style={{ zIndex: 101 }}
      >
        <nav
          className="navbar navbar-expand-lg navbar-light px-sm-4 px-xs-1 py-sm-2 bg-white"
          style={{ ...style, ...styles.body }}
          {...rest}
        >
          <div className="row d-flex align-items-center m-0 w-100">
            <div className="d-flex align-items-center d-block d-lg-none">
              <button
                onClick={handelTogggleDrawer}
                className="btn p-0 burger-icon burger-icon-left"
                id="kt_aside_mobile_toggle"
              >
                <span />
              </button>
            </div>
            <div
              className="d-block logo-image"
              style={{
                marginInlineStart: 15,
                marginInlineEnd: 80,
                cursor: "pointer",
              }}
              onClick={() => history.push("/members")}
            >
              <img
                src={HeaderLogoImage}
                alt={"..."}
                style={{ height: 50, width: 130, objectFit: "fill" }}
                name="logo_img"
              />
            </div>
            <Formik
              initialValues={{
                account: null,
                relation: "",
                observance: "english",
                first_adar_observance: false,
                second_adar_observance: true,
                address: "",
              }}
              validate={(values) => {
                const errors = {};
                if (!values.account && !email) {
                  errors.account = "Required";
                }
                if (!values.relation) {
                  errors.relation = "Required";
                }
                if (!values.address && email) {
                  errors.address = "Required";
                }
                return errors;
              }}
              onSubmit={(values, actions) => {
                const input = {
                  relation: values?.relation,
                  observance: values?.observance,
                  first_adar_observance: values?.first_adar_observance,
                  second_adar_observance: values?.second_adar_observance,
                };
                if (!email) {
                  input.first_name = values?.account?.first_name;
                  input.last_name = values?.account?.last_name;
                  input.member_id = values?.account?._id;
                } else {
                  input.email = email;
                  input.address = values?.address;
                }
                setMourners((prev) => [...prev, input]);
                actions.resetForm();
                actions.setFieldValue("relation", values?.relation);
                formik.setFieldTouched("mourners_list", true);
              }}
            >
              {({ values, errors, touched, setFieldValue }) => (
                <div className="top-search" style={{ width: "20%" }}>
                  <AsyncAutoCompleteField
                    {...{
                      name: "account",
                    }}
                    placeholder="Type 3 letters to start searching"
                    error={errors["account"]}
                    touched={touched["account"]}
                    onInputChange={(text) => {
                      setTypedSearch(text || "");
                      searchAction(text);
                    }}
                    classNamePrefix="react-select"
                    options={searchOptions}
                    isLoading={waitingForBackend}
                    handleChange={(e) => {
                      // setFieldValue("account", e);
                      setFieldValue(null);
                      if (e?.isClearRecentSearches) {
                        setRecentSearches(clearRecentSearches(recentSearchesKey));
                        return;
                      }
                      if (e?.type) {
                        setRecentSearches(addRecentSearch(recentSearchesKey, e));
                      }
                      if (isNavigationItem(e)) {
                        push(e?.url);
                      }
                      if (e?.type === "contact") {
                        push(
                          `/member-details/${e?.account_id}?q=${e?._id}&tab=Family`,
                          {
                            activeTab: "Family",
                          }
                        );
                      }
                      if (e?.type === "finance") {
                        push(`/finance?chargeId=${e?._id}`);
                      }
                      if (e?.type === "deceased") {
                        push(`/yahrzeit?q=${e?._id}`);
                      }
                      if (e?.type === "account") {
                        push(`/member-details/${e?._id}`, {
                          activeTab: "Account info",
                        });
                      }
                    }}
                    value={values.account}
                    onFocus={() => setEmail(null)}
                    onOptionNewTab={(e) => {
                      const url = searchResultUrl(e);
                      if (url) {
                        setRecentSearches(addRecentSearch(recentSearchesKey, e));
                        window.open(url, "_blank");
                      }
                    }}
                  />
                </div>
              )}
            </Formik>

            <div
              className="d-flex dropdown-main"
              style={{ marginInlineStart: "auto" }}
            >
              <HelpLinks />
              <Notifications
                notifications={notifications}
                isLoading={isNotificationLoading}
                markAsRead={markAsRead}
                openAddNotify={openAddNotify}
                fetchMoreNotifications={fetchMoreNotifications}
                hasMore={hasMore}
                totalNotifications={totalNotifications}
                markAllAsRead={openConfirmMarkAsReadHandler}
              />
              <MenuHeader openNotificationSettings={openNotificationSettings} />
            </div>
          </div>
        </nav>
      </header>
      <AlertDialog
        open={openConfirmMarkAsRead}
        onDecline={closeConfirmMarkAsReadHandler}
        onAccept={() => {
          markAllAsReadHandler();
        }}
        title={"Are you sure"}
        description={"Do you want to mark all notifications as read?"}
      />
    </>
  );
};
